import React, { useRef, useEffect, useCallback } from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import HoldAndWinCounterController from "./HoldAndWinCounterController";
import { safeInit } from "../../../utilities";
import {
  selectHoldAndWinActive,
  selectRespinsLeft,
  selectHoldAndWinSpinComplete,
} from "../../../State/selectors";
import { useHoldAndWinControl } from "../../../Contexts/HoldAndWinControlContext";

// Constants
const COUNTER_Y_OFFSET_MULTIPLIER = 0.8; // Position below symbol center

/**
 * HoldAndWinCounter - Displays remaining respins counter for Hold and Win feature
 * Shows animated transitions when counter value changes (3->2->1->0 or resets to 3)
 */
const HoldAndWinCounter = () => {
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const respinsLeft = useSelector(selectRespinsLeft);
  const holdAndWinSpinComplete = useSelector(selectHoldAndWinSpinComplete);

  const counterControllerRef = useRef(null);
  const counterContainerRef = useRef(null);
  const prevRespinsRef = useRef(null);
  const localCounterUpdatedRef = useRef(false); // Track if updated during spin

  const { registerProgressBarCallback } = useHoldAndWinControl();
  const { holdAndWinCounterRef } = useReelRefs();

  const dimensionsContext = useDimensions();
  const { dimensions, symbolHeight, device, orientation } = dimensionsContext;

  // Callback when a coin lands - update counter ONLY on first coin
  const handleCoinLanded = useCallback(() => {
    // Guard: Don't update if already at 3 (max respins)
    if (prevRespinsRef.current === 3) {
      return;
    }

    // Only update on FIRST coin landing during spin
    if (!holdAndWinSpinComplete && !localCounterUpdatedRef.current) {
      if (counterControllerRef.current) {
        // Reset counter to 3 when first coin lands
        counterControllerRef.current.updateCounter(3, prevRespinsRef.current);
        prevRespinsRef.current = 3;
        localCounterUpdatedRef.current = true; // Mark as updated
      }
    }
  }, [holdAndWinSpinComplete]);

  // Initialize controller when container is ready and Hold and Win becomes active
  useEffect(() => {
    if (
      counterContainerRef.current &&
      !counterControllerRef.current &&
      holdAndWinActive
    ) {
      safeInit(() => {
        counterControllerRef.current = new HoldAndWinCounterController(
          counterContainerRef.current,
          dimensions,
          symbolHeight,
          device,
          orientation
        );

        // Store ref in context for access by collection controller
        holdAndWinCounterRef.current = counterControllerRef.current;

        // Show counter immediately after initialization
        requestAnimationFrame(() => {
          if (
            counterControllerRef.current &&
            counterControllerRef.current.initialized &&
            counterControllerRef.current.counterSpine
          ) {
            if (respinsLeft !== undefined && respinsLeft !== null) {
              counterControllerRef.current.updateCounter(respinsLeft, null);
              prevRespinsRef.current = respinsLeft;
            } else {
              counterControllerRef.current.showCounter();
            }
          }
        });
      });
    }
  }, [
    holdAndWinActive,
    dimensions,
    symbolHeight,
    device,
    orientation,
    respinsLeft,
    holdAndWinCounterRef,
  ]);

  // Handle dimension changes
  useEffect(() => {
    if (counterControllerRef.current && symbolHeight && dimensions) {
      counterControllerRef.current.updateDimensions(
        dimensions,
        symbolHeight,
        device,
        orientation
      );
    }
  }, [symbolHeight, dimensions, device, orientation]);

  // Update counter when respinsLeft changes
  useEffect(() => {
    if (
      counterControllerRef.current &&
      holdAndWinActive &&
      respinsLeft !== undefined &&
      respinsLeft !== null &&
      prevRespinsRef.current !== respinsLeft
    ) {
      // Determine if this is a decrease (spin start) or increase (coin landed)
      const isDecrease =
        prevRespinsRef.current !== null && respinsLeft < prevRespinsRef.current;
      const isIncrease =
        prevRespinsRef.current !== null && respinsLeft > prevRespinsRef.current;

      // CRITICAL: Don't update to 3 if already at 3 (prevents retriggering on multiple coins)
      if (respinsLeft === 3 && prevRespinsRef.current === 3) {
        return;
      }

      // Update immediately if:
      // 1. Spin is complete (server reconciliation)
      // 2. Counter is decreasing (spin started, using a respin credit)
      // 3. Counter hasn't been updated locally during this spin yet (prevents double update)
      if (
        holdAndWinSpinComplete ||
        isDecrease ||
        (isIncrease && !localCounterUpdatedRef.current)
      ) {
        counterControllerRef.current.updateCounter(
          respinsLeft,
          prevRespinsRef.current
        );
        prevRespinsRef.current = respinsLeft;
      }
    }
  }, [respinsLeft, holdAndWinSpinComplete, holdAndWinActive]);

  // Reset previous respins when Hold and Win becomes inactive
  useEffect(() => {
    if (!holdAndWinActive) {
      prevRespinsRef.current = null;
      localCounterUpdatedRef.current = false;
    }
  }, [holdAndWinActive]);

  // Register callback for coin landings
  useEffect(() => {
    const unregister = registerProgressBarCallback(handleCoinLanded);
    return unregister; // Cleanup function removes this specific callback
  }, [registerProgressBarCallback, handleCoinLanded]);

  // Reset local update flag based on spin state
  useEffect(() => {
    if (!holdAndWinSpinComplete && holdAndWinActive) {
      // Spin is happening - flag should be ready for first coin
      // (flag should already be false from previous spin completion)
    } else if (holdAndWinSpinComplete) {
      // Spin finished - reset flag for next spin
      localCounterUpdatedRef.current = false;
    }
  }, [holdAndWinSpinComplete, holdAndWinActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (counterControllerRef.current) {
        counterControllerRef.current.cleanup();
      }
    };
  }, []);

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <Container
      name="hold_and_win_counter"
      x={0}
      y={symbolHeight * COUNTER_Y_OFFSET_MULTIPLIER}
      sortableChildren={true}
      zIndex={8}
      ref={counterContainerRef}
      visible={holdAndWinActive}
    />
  );
};

export default HoldAndWinCounter;
